import { useState, useEffect, useCallback } from "react";
import { BREAKPOINTS } from "@/lib/breakpoints";

/** Shared with useIsMobile so shells and pages never disagree. */
const MOBILE_BREAKPOINT = BREAKPOINTS.mobile;
const STORAGE_KEY = "acex_ui_mode";

type DeviceMode = "mobile" | "desktop";

export function useDeviceMode(): {
  mode: DeviceMode;
  isMobile: boolean;
  override: DeviceMode | null;
  setOverride: (mode: DeviceMode | null) => void;
} {
  const [naturalMode, setNaturalMode] = useState<DeviceMode>(() =>
    typeof window !== "undefined" && window.innerWidth < MOBILE_BREAKPOINT ? "mobile" : "desktop"
  );

  const [override, setOverrideState] = useState<DeviceMode | null>(() => {
    if (typeof window === "undefined") return null;
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === "mobile" || stored === "desktop" ? stored : null;
  });

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const onChange = () => setNaturalMode(window.innerWidth < MOBILE_BREAKPOINT ? "mobile" : "desktop");
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  const setOverride = useCallback((mode: DeviceMode | null) => {
    setOverrideState(mode);
    if (mode) {
      localStorage.setItem(STORAGE_KEY, mode);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const mode = override ?? naturalMode;

  return { mode, isMobile: mode === "mobile", override, setOverride };
}
